import React, { useRef, useCallback, useMemo, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import JoditEditor from 'jodit-react';

const RichTextEditor = forwardRef(({
  value = '',
  onChange,
  placeholder = 'Enter your text here...',
  height = 300,
  disabled = false,
  className = ''
}, ref) => {
  const editor = useRef(null);
  const [internalValue, setInternalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  // Expose method to get current content directly from editor instance
  useImperativeHandle(ref, () => ({
    getContent: () => {
      // CRITICAL: Try to get from Jodit instance first (most reliable for current content)
      try {
        if (editor.current) {
          // jodit-react v5+ exposes the Jodit instance directly
          // Try accessing the Jodit instance's value
          const joditInstance = editor.current;
          
          // Method 1: Direct value property (most common)
          if (joditInstance.value !== undefined && joditInstance.value !== null) {
            const content = joditInstance.value;
            console.log("[RichTextEditor] getContent: Using editor.current.value", {
              length: content.length,
              preview: content.substring(0, 50)
            });
            return content;
          }
          
          // Method 2: Via jodit property (some versions)
          if (joditInstance.jodit && joditInstance.jodit.value !== undefined) {
            const content = joditInstance.jodit.value;
            console.log("[RichTextEditor] getContent: Using editor.current.jodit.value", {
              length: content.length,
              preview: content.substring(0, 50)
            });
            return content;
          }
          
          // Method 3: Get from editor element
          if (joditInstance.element && joditInstance.element.innerHTML) {
            const content = joditInstance.element.innerHTML;
            console.log("[RichTextEditor] getContent: Using editor.element.innerHTML", {
              length: content.length,
              preview: content.substring(0, 50)
            });
            return content;
          }
        }
      } catch (e) {
        console.warn("RichTextEditor: Error accessing editor instance", e);
      }
      
      // Fallback: Use internalValue (updated on onChange)
      if (internalValue !== undefined && internalValue !== null) {
        console.log("[RichTextEditor] getContent: Using internalValue fallback", {
          length: internalValue.length,
          preview: internalValue.substring(0, 50)
        });
        return internalValue;
      }
      
      // Final fallback
      console.warn("[RichTextEditor] getContent: All methods failed, returning empty string");
      return '';
    }
  }));

  // Update internal value when external value changes
  // Use a ref to track if we should allow value updates (prevent resets during editing)
  const allowValueUpdate = useRef(true);
  
  useEffect(() => {
    // Only update if explicitly allowed (prevents unwanted resets during editing)
    if (allowValueUpdate.current && value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const config = useMemo(() => ({

    readonly: disabled,
    placeholder: placeholder,
    height: height,
    toolbar: isFocused,
    toolbarSticky: false,
    toolbarDisableStickyForMobile: true,
    spellcheck: true,
    language: 'en',
    toolbarButtonSize: 'medium',
    toolbarAdaptive: false,
    showCharsCounter: true,
    showWordsCounter: true,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_clear_html',
    buttons: [
      'bold', 'italic', 'underline', '|',
      'ul', 'ol', '|',
      'link', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser'
    ],
    removeButtons: [
      'fontsize',
      'superscript',
      'subscript',
      'font',
      'brush',
      'paragraph',
      'copyformat',
      'image',
      'table',
      'symbol',
      'fullsize',
      'print',
      'about'
    ],
    uploader: {
      insertImageAsBase64URI: true
    },
    // Add these settings to prevent focus issues
    autofocus: false,
    saveModeInStorage: false,
    disablePlugins: ['mobile', 'resizer'],
    // Prevent editor from losing focus
    allowResizeX: false,
    allowResizeY: false,
    resizeUseStore: false,
    // Event handlers for focus/blur
    events: {
      focus: () => {
        setIsFocused(true);
      },
      blur: () => {
        setIsFocused(false);
      }
    }
  }), [disabled, placeholder, height, isFocused]);

  // Use useCallback to prevent unnecessary re-renders
  const handleChange = useCallback((newContent) => {
    // Always update internal state first
    const content = newContent || '';
    // Prevent value prop from overwriting our changes
    allowValueUpdate.current = false;
    setInternalValue(content);
    // Then notify parent component
    if (onChange) {
      onChange(content);
    }
    console.log("[RichTextEditor] handleChange:", {
      length: content.length,
      preview: content.substring(0, 100),
      full: content
    });
    // Re-enable value updates after a short delay
    setTimeout(() => {
      allowValueUpdate.current = true;
    }, 100);
  }, [onChange]);

  const handleBlur = useCallback((newContent) => {
    // Always update internal state first
    const content = newContent || '';
    setInternalValue(content);
    // Then notify parent component
    if (onChange) {
      onChange(content);
    }
    console.log("[RichTextEditor] handleBlur:", {
      length: content.length,
      preview: content.substring(0, 50)
    });
  }, [onChange]);

  return (
    <div className={`rich-text-editor-wrapper ${className}`} style={{
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      overflow: 'hidden'
    }}>
      <JoditEditor
        ref={editor}
        value={internalValue}
        config={config}
        tabIndex={1}
        onBlur={handleBlur}
        onChange={handleChange}
        // Add key prop to prevent unnecessary re-mounting
        key={`jodit-${height}-${disabled}`}
      />
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;